import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { ICaixaItem } from '@model/CaixaItem';
import { EnumCaixaTipoLancamento } from '@model/CaixaTipoLancamento';
import { ICaixaRegistrarRequest, useCaixaService } from '@services/CaixaService';
import { yup } from '@validations/Yup';
import React from 'react';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { CaixaRegistrarForm } from './CaixaRegistrarForm';
import { useField } from '@hooks/useField';

export type CaixaRegistrarModalProps = {
    modal: IModalWindow
    caixaTipoLancamento: EnumCaixaTipoLancamento
    onCaixaItemRegistrado: (caixaItem: ICaixaItem) => void
}

export function CaixaRegistrarModal ({ modal, caixaTipoLancamento, onCaixaItemRegistrado }: CaixaRegistrarModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const caixaService = useCaixaService();

    const form = useForm<ICaixaRegistrarRequest>({
        descricao: useField(""),
        valor: useField(0),
    });

    const validation = useValidation<Partial<ICaixaRegistrarRequest>>({
        descricao: yup.string().trim().required(),
        valor: yup.number().required().moreThan(0)
    });
    
    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            form.clearErrors();
            validation.validate(values)
                .then(_ => {
                    loading.start();
                    caixaService.registrar(caixaTipoLancamento, values)
                        .then(response => {
                            message.success(`${caixaTipoLancamento === EnumCaixaTipoLancamento.Credito ? "Crédito" : "Débito"} registrado com sucesso`);
                            onCaixaItemRegistrado(response.data);
                            modal.close();
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [caixaService, caixaTipoLancamento, form, loading, message, modal, onCaixaItemRegistrado, validation]);

    return (
        <ModalWindow
            modal={modal}
            disabled={loading.isLoading}
            TransitionProps={{
                onEnter: form.reset
            }}
        >
            <ModalWindowTitle
                icon={caixaTipoLancamento === EnumCaixaTipoLancamento.Credito ? <AddCircleIcon style={{color: 'darkgreen'}} /> : <RemoveCircleIcon color="secondary" />}
                title={caixaTipoLancamento === EnumCaixaTipoLancamento.Credito ? "Registrar Crédito de Caixa" : "Registrar Débito de Caixa"}
            />

            <ModalWindowContent>
                <CaixaRegistrarForm
                    form={form}
                />
            </ModalWindowContent>

            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm}>{caixaTipoLancamento === EnumCaixaTipoLancamento.Credito ? "Registrar Crédito" : "Registrar Débito"}</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}